import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useClickOutside } from '@/hooks/use-click-outside';
import { isEditorPortalTarget } from '@/lib/editor-portal-targets';

export const SELECT_CLICK_DELAY_MS = 250;

export interface UseSelectThenEditFieldOptions<T extends HTMLElement = HTMLDivElement> {
  rootRef?: RefObject<T | null>;
  dismissOnEditorBlur?: boolean;
  onActivate?: () => void;
  onDismissStart?: (outsideTarget?: EventTarget | null) => void;
  onDismissEnd?: (outsideTarget?: EventTarget | null) => void;
  onEditorFocusChange?: (
    focused: boolean,
    dismiss: (outsideTarget?: EventTarget | null) => void,
  ) => void;
  registerDismiss?: (dismiss: (outsideTarget?: EventTarget | null) => void) => void;
}

export function useSelectThenEditField<T extends HTMLElement = HTMLDivElement>(
  options: UseSelectThenEditFieldOptions<T> = {},
) {
  const {
    rootRef: rootRefOption,
    dismissOnEditorBlur = true,
    onActivate,
    onDismissStart,
    onDismissEnd,
    onEditorFocusChange: onEditorFocusChangeOption,
    registerDismiss,
  } = options;

  const internalRef = useRef<T | null>(null);
  const rootRef: RefObject<T | null> = rootRefOption ?? internalRef;
  const suppressOutsideDismissRef = useRef(false);
  const pendingSelectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerSelectRef = useRef(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const cancelPendingSelect = useCallback(() => {
    if (pendingSelectTimeoutRef.current) {
      clearTimeout(pendingSelectTimeoutRef.current);
      pendingSelectTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cancelPendingSelect();
    };
  }, [cancelPendingSelect]);

  const dismiss = useCallback(
    (outsideTarget?: EventTarget | null) => {
      if (suppressOutsideDismissRef.current) {
        return;
      }
      cancelPendingSelect();
      onDismissStart?.(outsideTarget);
      setIsSelected(false);
      setIsEditing(false);
      onDismissEnd?.(outsideTarget);
    },
    [cancelPendingSelect, onDismissEnd, onDismissStart],
  );

  useEffect(() => {
    registerDismiss?.(dismiss);
  }, [dismiss, registerDismiss]);

  const selectField = useCallback(() => {
    onActivate?.();
    setIsSelected(true);
  }, [onActivate]);

  const handleEnterEdit = useCallback(
    (event?: MouseEvent) => {
      cancelPendingSelect();
      event?.stopPropagation();
      onActivate?.();
      setIsSelected(false);
      setIsEditing(true);
    },
    [cancelPendingSelect, onActivate],
  );

  const handleExitEditing = useCallback(() => {
    suppressOutsideDismissRef.current = true;
    cancelPendingSelect();
    setIsEditing(false);
    setIsSelected(true);
    onActivate?.();
    const element = rootRef.current;
    element?.focus();
    requestAnimationFrame(() => {
      suppressOutsideDismissRef.current = false;
    });
  }, [cancelPendingSelect, onActivate, rootRef]);

  useClickOutside(rootRef, {
    enabled: isSelected || isEditing,
    capture: true,
    dismissOnFocus: true,
    shouldIgnoreTarget: isEditorPortalTarget,
    onDismiss: dismiss,
  });

  const handleEditorFocusChange = useCallback(
    (focused: boolean) => {
      if (focused) {
        return;
      }
      if (onEditorFocusChangeOption) {
        onEditorFocusChangeOption(focused, dismiss);
        return;
      }
      if (dismissOnEditorBlur) {
        dismiss();
      }
    },
    [dismiss, dismissOnEditorBlur, onEditorFocusChangeOption],
  );

  const handleShellClick = useCallback(() => {
    pointerSelectRef.current = false;
    if (isEditing || isSelected) {
      return;
    }
    cancelPendingSelect();
    pendingSelectTimeoutRef.current = setTimeout(() => {
      pendingSelectTimeoutRef.current = null;
      selectField();
    }, SELECT_CLICK_DELAY_MS);
  }, [cancelPendingSelect, isEditing, isSelected, selectField]);

  const handleShellFocus = useCallback(() => {
    if (isEditing || isSelected) {
      return;
    }
    if (pointerSelectRef.current) {
      return;
    }
    cancelPendingSelect();
    selectField();
  }, [cancelPendingSelect, isEditing, isSelected, selectField]);

  const handleShellPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (isEditing) {
        event.stopPropagation();
        return;
      }
      pointerSelectRef.current = true;
    },
    [isEditing],
  );

  const handleShellKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isEditing) {
        return;
      }
      if (event.key === 'Escape' && isSelected) {
        event.preventDefault();
        dismiss();
      }
    },
    [dismiss, isEditing, isSelected],
  );

  const shellEventHandlers = useMemo(
    () => ({
      onPointerDown: handleShellPointerDown,
      onFocus: handleShellFocus,
      onClick: handleShellClick,
      onDoubleClick: !isEditing ? handleEnterEdit : undefined,
      onKeyDown: handleShellKeyDown,
    }),
    [
      handleEnterEdit,
      handleShellClick,
      handleShellFocus,
      handleShellKeyDown,
      handleShellPointerDown,
      isEditing,
    ],
  );

  return {
    rootRef,
    isSelected,
    isEditing,
    showEditToolbar: isSelected && !isEditing,
    tabIndex: isEditing ? -1 : 0,
    handleEnterEdit,
    handleExitEditing,
    handleEditorFocusChange,
    shellEventHandlers,
  };
}
